import React from 'react';
import { Button } from '../ui/Button';
import { CheckCircle2, Gift, BookOpen, Headphones, Lock, Mail, ShieldCheck, ArrowRight } from 'lucide-react';
const BONO_1_IMG = 'https://cdn.magicpatterns.com/uploads/akUF7sZ2bSUsDMZbTAJ3x6/A-Bono1.png';
const BONO_2_IMG = 'https://cdn.magicpatterns.com/uploads/nqaKsf71BXgdKLXQovPgkt/A-Bono2.png';
const BONO_3_IMG = 'https://cdn.magicpatterns.com/uploads/tMCLMhmjaodZLpKJbXEV31/A-Bono3.png';
const bonuses = [{
  number: 1,
  image: BONO_1_IMG,
  title: 'Modo Oficina: Calma Invisible en 2 Minutos',
  value: 'AR$ 15.000',
  hook: '¿Te agarra ansiedad en una reunión, en Zoom o en el trabajo presencial y lo último que querés es que alguien lo note?',
  description: 'Tu protocolo discreto de rescate para el trabajo.',
  items: ['5 técnicas invisibles que podés hacer sentada frente a tu jefe sin que nadie lo note', 'Los 5 scripts exactos para ganar tiempo cuando te hablan justo cuando estás al borde', 'El Plan Baño SOS: cómo salir, regularte en 90 segundos y volver como si nada', 'Checklist "Rescate Express" para guardar en el celu y usar al instante', 'Plantilla "Mi Secuencia Personal" para armar tu protocolo único'],
  forWho: 'Para las que necesitan seguir funcionando en el trabajo aunque por dentro estén a mil.',
  accentColor: 'border-blue-500',
  badgeColor: 'bg-blue-600'
}, {
  number: 2,
  image: BONO_2_IMG,
  title: 'Kit Anti-Rumiación Nocturna: Apagá la Cabeza en 7 Minutos',
  value: 'AR$ 17.000',
  hook: '¿Apagás la luz y tu cabeza se prende? ¿Revisás conversaciones del día, pensás en plata, en el laburo, en todo lo que no resolviste?',
  description: 'Un ritual de 7 minutos para cerrar el día y que tu cuerpo entre en modo descanso de verdad.',
  items: ['El Ritual Exacto de 7 Minutos (paso a paso, con timer)', 'Plantilla "Descarga Mental": sacá todo de la cabeza en 2 minutos', 'La tabla "Me preocupa / Acción mínima / Lo suelto por hoy" para cerrar pendientes sin resolverlos a la fuerza', 'Plan B de 90 segundos para las noches que estás muy acelerada', '10 prompts ultrarrápidos para cuando no sabés ni por dónde empezar', 'Tracker 7 noches para que veas tu progreso real'],
  forWho: 'Para las que se acuestan cansadas pero no pueden dormir porque la cabeza no para.',
  accentColor: 'border-indigo-500',
  badgeColor: 'bg-indigo-600'
}, {
  number: 3,
  image: BONO_3_IMG,
  title: 'Mapa de Disparadores: Detectá Tu Ansiedad Antes de Que Explote',
  value: 'AR$ 15.000',
  hook: '¿Y si pudieras frenar la ansiedad antes de que llegue al pico?',
  description: 'Aprendé a leer tus propias señales tempranas y tener un plan listo para cuando aparecen.',
  items: ['Los 4 tipos de disparadores (cuerpo, entorno, mente, contexto) con ejemplos reales', 'Ejercicio guiado para identificar tus 5 disparadores principales en 10 minutos', 'Checklist de señales tempranas (tu alarma corporal antes del pico)', 'El método "Si/Entonces": tu plan automático de prevención', 'Ejemplos completos y listos para copiar', 'Tracker semanal para ver patrones sin culparte', 'Resumen en 1 hoja: tus disparadores + señales + plan (para imprimir o guardar)'],
  forWho: 'Para las que quieren dejar de "apagar incendios" y empezar a prevenir.',
  accentColor: 'border-violet-500',
  badgeColor: 'bg-violet-600'
}];
const valueStack = [{
  icon: BookOpen,
  label: 'Kit SOS Ansiedad (Ebook — 21 técnicas)',
  value: 'AR$ 15.000',
  included: true,
  free: false
}, {
  icon: Headphones,
  label: 'Audio-Guía de Rescate (5 minutos)',
  value: 'AR$ 7.000',
  included: true,
  free: false
}, {
  icon: Gift,
  label: 'Bono #1: Modo Oficina',
  value: 'AR$ 15.000',
  included: true,
  free: true
}, {
  icon: Gift,
  label: 'Bono #2: Kit Anti-Rumiación Nocturna',
  value: 'AR$ 17.000',
  included: true,
  free: true
}, {
  icon: Gift,
  label: 'Bono #3: Mapa de Disparadores',
  value: 'AR$ 15.000',
  included: true,
  free: true
}];
export function BonusSection({
  isLp1
}: {
  isLp1?: boolean;
}) {
  const basePrice = isLp1 ? 17800 : 8900;
  const formattedPrice = basePrice.toLocaleString('es-AR');
  return <section className="bg-slate-50 py-16 sm:py-24" data-id="element-75">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-76">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center mb-16" data-id="element-77">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-100 px-4 py-1.5 text-sm font-semibold text-amber-700 ring-1 ring-inset ring-amber-300 mb-4" data-id="element-78">
            <Gift className="w-4 h-4" data-id="element-79" />
            Bonos Exclusivos
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 font-serif mb-4 leading-tight" data-id="element-80">
            Pero eso no es todo. Cuando decidas llevar el Kit SOS Ansiedad hoy,
            te sumamos 3 bonos exclusivos que normalmente no están disponibles.
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-81">
            Sé que cuando la ansiedad aparece, no llega sola. Llega en la
            oficina, llega a la noche cuando querés dormir, llega antes de que
            puedas evitarla. Por eso armé estos 3 bonos para cubrir exactamente
            esos momentos.
          </p>
        </div>

        {/* Bonus cards */}
        <div className="mx-auto max-w-4xl space-y-10 mb-20" data-id="element-82">
          {bonuses.map(bonus => <div key={bonus.number} className={`bg-white rounded-2xl shadow-sm border-t-4 ${bonus.accentColor} overflow-hidden`} data-id="element-83">
              <div className="flex flex-col md:flex-row gap-0" data-id="element-84">
                {/* Image */}
                <div className="md:w-56 flex-shrink-0 bg-slate-100 flex items-center justify-center p-6" data-id="element-85">
                  <img src={bonus.image} alt={bonus.title} className="w-full max-w-[160px] md:max-w-full h-auto object-contain drop-shadow-lg" data-id="element-86" />
                </div>

                {/* Content */}
                <div className="flex-1 p-6 md:p-8" data-id="element-87">
                  <div className="flex items-start justify-between gap-4 mb-3 flex-wrap" data-id="element-88">
                    <div className="flex items-center gap-3" data-id="element-89">
                      <span className={`${bonus.badgeColor} text-white text-xs font-bold px-3 py-1 rounded-full`} data-id="element-90">
                        BONO #{bonus.number}
                      </span>
                      <span className="text-sm text-slate-400 line-through" data-id="element-91">
                        {bonus.value}
                      </span>
                      <span className="text-sm font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full" data-id="element-92">
                        GRATIS
                      </span>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-slate-900 font-serif mb-2 leading-snug" data-id="element-93">
                    {bonus.title}
                  </h3>
                  <p className="text-slate-500 italic text-sm mb-3" data-id="element-94">
                    {bonus.hook}
                  </p>
                  <p className="text-slate-700 mb-4" data-id="element-95">{bonus.description}</p>

                  <ul className="space-y-2 mb-5" data-id="element-96">
                    {bonus.items.map((item, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-600" data-id="element-97">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-98" />
                        <span data-id="element-99">{item}</span>
                      </li>)}
                  </ul>

                  <div className="bg-slate-50 rounded-lg px-4 py-3 border border-slate-100" data-id="element-100">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-0.5" data-id="element-101">
                      Para quién es
                    </p>
                    <p className="text-sm text-slate-700 italic" data-id="element-102">
                      {bonus.forWho}
                    </p>
                  </div>
                </div>
              </div>
            </div>)}
        </div>

        {/* Value stack table */}
        <div className="mx-auto max-w-2xl mb-14" data-id="element-103">
          <h3 className="text-2xl font-bold text-slate-900 font-serif text-center mb-6" data-id="element-104">
            Todo lo que recibís hoy
          </h3>
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-105">
            <div className="grid grid-cols-3 bg-slate-800 text-white text-xs font-semibold uppercase tracking-wide px-5 py-3" data-id="element-106">
              <span data-id="element-107">Producto / Bono</span>
              <span className="text-center" data-id="element-108">Valor real</span>
              <span className="text-center" data-id="element-109">Hoy</span>
            </div>
            {valueStack.map((item, i) => <div key={i} className={`grid grid-cols-3 items-center px-5 py-4 text-sm border-b border-slate-100 last:border-0 ${item.free ? 'bg-green-50/50' : ''}`} data-id="element-110">
                <div className="flex items-center gap-2 pr-2" data-id="element-111">
                  <item.icon className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-112" />
                  <span className="text-slate-700 leading-tight" data-id="element-113">
                    {item.label}
                  </span>
                </div>
                <span className="text-center text-slate-400 line-through text-xs" data-id="element-114">
                  {item.value}
                </span>
                <span className="text-center font-semibold text-green-600 text-xs" data-id="element-115">
                  {item.free ? 'GRATIS' : 'Incluido'}
                </span>
              </div>)}
            <div className="grid grid-cols-3 items-center px-5 py-4 bg-slate-900 text-white" data-id="element-116">
              <span className="font-bold text-sm col-span-1" data-id="element-117">VALOR TOTAL</span>
              <span className="text-center text-slate-400 line-through text-sm" data-id="element-118">
                AR$ 69.000
              </span>
              <span className="text-center font-bold text-amber-400 text-lg" data-id="element-119">
                AR$ {formattedPrice}
              </span>
            </div>
          </div>
        </div>

        {/* Closing paragraph + CTA */}
        <div className="mx-auto max-w-xl text-center" data-id="element-120">
          <p className="text-slate-600 text-lg leading-relaxed mb-8" data-id="element-121">
            No vas a tener que esperar días, ni ir a buscar un paquete. Apenas
            confirmás tu pago, recibís todo en tu email en menos de 1 minuto. El
            Kit, el Audio y los 3 Bonos listos en tu celu para cuando los
            necesites.
          </p>

          <p className="text-sm font-semibold text-slate-500 uppercase tracking-widest mb-4" data-id="element-122">
            Sí, quiero el Kit completo + los 3 bonos gratis
          </p>

          <a href="#oferta-cta" onClick={e => {
          e.preventDefault();
          document.getElementById('oferta-cta')?.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }} className="inline-flex items-center justify-center rounded-lg font-bold transition-colors bg-blue-600 text-white hover:bg-blue-700 shadow-xl shadow-blue-500/20 h-14 px-10 text-base sm:text-lg w-full" data-id="element-123">
            QUIERO MI KIT SOS AHORA — AR$ {formattedPrice}
            <ArrowRight className="ml-2 h-5 w-5 flex-shrink-0" data-id="element-124" />
          </a>

          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3 text-sm text-slate-500" data-id="element-125">
            <span className="flex items-center gap-1.5" data-id="element-126">
              <Lock className="w-3.5 h-3.5" data-id="element-127" />
              Pago 100% seguro vía Mercado Pago
            </span>
            <span className="hidden sm:block text-slate-300" data-id="element-128">·</span>
            <span className="flex items-center gap-1.5" data-id="element-129">
              <Mail className="w-3.5 h-3.5" data-id="element-130" />
              Acceso inmediato desde tu celular
            </span>
            <span className="hidden sm:block text-slate-300" data-id="element-131">·</span>
            <span className="flex items-center gap-1.5" data-id="element-132">
              <ShieldCheck className="w-3.5 h-3.5" data-id="element-133" />
              Garantía de 7 días
            </span>
          </div>
        </div>
      </div>
    </section>;
}